import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { SentinelService } from "../../services/SentinelService";
import NewSentinelForm from "./NewSentinelForm";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { XIcon } from "lucide-react";

const RegisterSentinel: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const { intentGroupId } = useParams();
  const [loading, setLoading] = useState(false);
  const [qa, setQa] = useState<any>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleSubmit = (formData: { 
    expectation: string; 
    condition: string; 
    qa?: any;
  }) => {
    setLoading(true);    
    const sentinelData: {
      intent_group_id: number;
      expectation: string;
      condition: string;
      qa?: any;
    } = {
      intent_group_id: Number(intentGroupId),
      expectation: formData.expectation,
      condition: formData.condition,
      qa: formData.qa,
    };

    SentinelService.generateSentinel(sentinelData, setLoading, apiKey).subscribe(
      (response: any) => {
        setHasSubmitted(true);
        const sentinels = response.sentinels;
        const newQa = response.user_prompts;
        if (newQa && newQa.length > 0) {
          // There are questions that the user needs to answer
          setQa(qa ? [...qa, ...newQa] : newQa);
        } else if (sentinels && sentinels.length > 0) {
          const createdSentinel = sentinels[0];
          // TODO: Replace with toast notification when available
          console.log("Sentinel registered successfully");
          navigate(`/intent-group/${intentGroupId}/sentinel/${createdSentinel.id}`);
        }
      },
      (error: any) => {
        console.error(error);
        // TODO: Replace with toast notification when available
        console.error("Failed to generate Sentinel");
        setLoading(false);
      }
    );
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/intent-groups")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Intent Groups
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/intent-group/${intentGroupId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Intent Group
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">New Sentinel</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Generate New Sentinel
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Create a new sentinel to monitor and validate model outputs
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="gap-2"
              >
                <XIcon className="w-4 h-4" />
                Close
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Form Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <NewSentinelForm
              loading={loading}
              onSubmit={handleSubmit}
              onClose={handleClose}
              qaItems={qa}
              setQaItems={setQa}
              hasSubmitted={hasSubmitted}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterSentinel;
